/**
 * WHAT:
 *   File handling utility functions for PDF operations.
 *
 * WHY:
 *   Centralized file operations for consistent PDF handling across the TestTab component.
 *
 * DOMAIN: Investments → Parqet → Test Tab → Utils → File Helpers
 *
 * DEPENDENCIES:
 *   - Tauri core API (invoke)
 *   - Tauri opener plugin (openPath)
 *   - FileReader API (browser)
 *
 * RISKS:
 *   - NOTE: Large PDF files may cause memory issues during Base64 conversion
 *   - WARN: FileReader operations are async - ensure proper error handling
 *   - NOTE: File paths may be invalid on different operating systems
 *   - NOTE: PDF opening may fail if file is locked or doesn't exist
 */

import { invoke } from "@tauri-apps/api/core";
import { openPath } from "@tauri-apps/plugin-opener";

import { debugLog } from "@/domains/logging/utils/debugLog";

/**
 * WHAT:
 *   Safe Base64 conversion for binary PDFs.
 *
 * WHY:
 *   Converts File objects to Base64 strings for backend processing.
 *
 * INPUTS:
 *   - file: File — PDF file to convert
 *
 * OUTPUT:
 *   Promise<string> — Base64 encoded string of the file content.
 *
 * @param file - PDF file to convert
 *
 * @returns {Promise<string>} Base64 encoded string of the file content
 *
 */
export const readFileAsBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      const result = reader.result as string;
      const base64 = result.split(",")[1];
      if (base64 === undefined) {
        reject(new Error("Failed to extract base64 data"));
        return;
      }
      resolve(base64);
    });
    reader.addEventListener("error", () => {
      reject(new Error("FileReader error"));
    });
    reader.readAsDataURL(file);
  });
};

/**
 * WHAT:
 *   Extract plain text from PDF file.
 *
 * WHY:
 *   Delegates PDF text extraction to Rust backend for consistent parsing.
 *
 * INPUTS:
 *   - file: File — PDF file to extract text from
 *
 * OUTPUT:
 *   Promise<string> — Extracted plain text from PDF.
 *
 * @param file - PDF file to extract text from
 *
 * @returns {Promise<string>} Extracted plain text from PDF
 *
 */
export const extractRawText = async (file: File): Promise<string> => {
  const base64 = await readFileAsBase64(file);
  return invoke<string>("extract_pdf_text", {
    fileName: file.name,
    pdfData: base64,
  });
};

/**
 * WHAT:
 *   Open PDF file in default application.
 *
 * WHY:
 *   Allows users to view PDF files in their default PDF viewer.
 *
 * INPUTS:
 *   - filePath: string — Path to PDF file
 *
 * OUTPUT:
 *   Promise<void> — Resolves when file is opened.
 *
 * SIDE EFFECTS:
 *   - Opens external PDF viewer application
 *   - Logs operation to debug system
 *
 * RISKS:
 *   - NOTE: May fail if file path is invalid or application not found
 *
 * @param filePath
 *
 */
export const openPdfFile = async (filePath: string): Promise<void> => {
  try {
    await openPath(filePath);
    debugLog("info", "investments", "PDF opened", {
      filePath,
      module: "parqet",
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error) || "Unbekannter Fehler";
    // Error is re-thrown, no need for console.error
    throw new Error(`Fehler beim Öffnen der PDF: ${message}`);
  }
};
